import React, { useEffect, useRef, useState } from "react";
import {
    getDeviceByTracking,
    getDevicePosition,
    getDeviceOnlineStatus,
    getDeviceRoute,
} from "../../../api/iotClientService";

export default function ClientIoTTrackingPage() {
    const [trackingNumber, setTrackingNumber] = useState("");
    const [device, setDevice] = useState(null);
    const [position, setPosition] = useState(null);
    const [online, setOnline] = useState(null);
    const [route, setRoute] = useState([]);
    const [eta, setEta] = useState(null);
    const [arrived, setArrived] = useState(false);
    const [wsStatus, setWsStatus] = useState("Disconnected");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const wsRef = useRef(null);

    const handleSearch = async (e) => {
        e.preventDefault();

        if (!trackingNumber.trim()) {
            setError("Tracking number is required.");
            return;
        }

        try {
            setLoading(true);
            setError("");
            setDevice(null);
            setPosition(null);
            setOnline(null);
            setRoute([]);
            setEta(null);
            setArrived(false);
            setWsStatus("Disconnected");

            const foundDevice = await getDeviceByTracking(trackingNumber.trim());
            setDevice(foundDevice);

            const [pos, onlineStatus, routeData] = await Promise.all([
                getDevicePosition(foundDevice.id),
                getDeviceOnlineStatus(foundDevice.id),
                getDeviceRoute(foundDevice.id),
            ]);

            setPosition(pos);
            setOnline(onlineStatus);
            setRoute(Array.isArray(routeData) ? routeData : []);

            connectWs(foundDevice.trackingNumber);
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "IoT device not found for this tracking number."
            );
        } finally {
            setLoading(false);
        }
    };

    const connectWs = (tracking) => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }

        const token =
            localStorage.getItem("authToken") ||
            sessionStorage.getItem("authToken") ||
            localStorage.getItem("token") ||
            sessionStorage.getItem("token");

        if (!token) {
            setError("Unauthorized.");
            return;
        }

        const wsBaseUrl =
            import.meta.env.VITE_WS_URL || "wss://api.idlogichain.com";

        const url = `${wsBaseUrl}/ws/iot?access_token=${encodeURIComponent(
            token
        )}&trackingNumber=${encodeURIComponent(tracking)}`;

        const ws = new WebSocket(url);

        setWsStatus("Connecting");

        ws.onopen = () => setWsStatus("Connected");

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);

                if (msg.type !== "iot_update") return;

                setPosition({
                    latitude: msg.latitude,
                    longitude: msg.longitude,
                    updatedAt: msg.updatedAt,
                });

                setEta(msg.eta);
                setArrived(Boolean(msg.arrived));
                setWsStatus("Connected");
            } catch (e) {
                console.error("WS parse error", e);
            }
        };

        ws.onerror = () => setWsStatus("Error");
        ws.onclose = () => setWsStatus("Disconnected");

        wsRef.current = ws;
    };

    useEffect(() => {
        return () => {
            wsRef.current?.close();
        };
    }, []);

    return (
        <div className="w-full">
            <div className="mb-8">
                <h1 className="text-3xl font-black text-slate-900">
                    IoT Shipment Tracking
                </h1>
                <p className="text-slate-600 mt-2">
                    Track live shipment location, route history, online status and ETA.
                </p>
            </div>

            <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 mb-6">
                <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
                    <input
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="Enter tracking number"
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />

                    <button
                        type="submit"
                        className="rounded-xl bg-indigo-600 px-6 py-3 text-white font-semibold hover:bg-indigo-700 transition disabled:opacity-60"
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Track Shipment"}
                    </button>
                </form>

                {error && (
                    <div className="mt-4 rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                        {error}
                    </div>
                )}
            </div>

            {!device && !loading && (
                <div className="bg-white rounded-2xl shadow border border-slate-200 p-10 text-center">
                    <div className="text-5xl mb-4">📡</div>
                    <h2 className="text-2xl font-bold">No Tracking Loaded</h2>
                    <p className="text-slate-500 mt-2">
                        Enter a tracking number to view live shipment telemetry.
                    </p>
                </div>
            )}

            {device && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
                        <StatCard
                            title="Tracking Number"
                            value={device.trackingNumber || trackingNumber}
                            subtitle="Shipment tracking identifier"
                            color="blue"
                        />

                        <StatCard
                            title="Device Status"
                            value={online?.isOnline ? "Online" : "Offline"}
                            subtitle="Current IoT connectivity"
                            color={online?.isOnline ? "green" : "red"}
                        />

                        <StatCard
                            title="WebSocket"
                            value={wsStatus}
                            subtitle="Live telemetry stream"
                            color={wsStatus === "Connected" ? "green" : wsStatus === "Error" ? "red" : "slate"}
                        />

                        <StatCard
                            title="Delivery"
                            value={arrived ? "Delivered" : "In Transit"}
                            subtitle="Shipment movement status"
                            color={arrived ? "green" : "yellow"}
                        />
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
                        <div className="xl:col-span-2 bg-white rounded-2xl shadow border border-slate-200 p-6">
                            <h2 className="text-2xl font-bold mb-5">
                                Live Position
                            </h2>

                            {position ? (
                                <div className="grid md:grid-cols-2 gap-4">
                                    <InfoRow label="Latitude" value={position.latitude ?? "—"} />
                                    <InfoRow label="Longitude" value={position.longitude ?? "—"} />
                                    <InfoRow
                                        label="Updated"
                                        value={
                                            position.updatedAt
                                                ? new Date(position.updatedAt).toLocaleString()
                                                : "—"
                                        }
                                    />
                                    <InfoRow
                                        label="Google Maps"
                                        value={
                                            position.latitude && position.longitude ? (
                                                <a
                                                    href={`https://www.google.com/maps?q=${position.latitude},${position.longitude}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-blue-600 hover:underline"
                                                >
                                                    Open location
                                                </a>
                                            ) : (
                                                "—"
                                            )
                                        }
                                    />
                                </div>
                            ) : (
                                <EmptyText text="No live position available yet." />
                            )}
                        </div>

                        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6">
                            <h2 className="text-2xl font-bold mb-5">
                                ETA
                            </h2>

                            {eta ? (
                                <div className="space-y-3">
                                    <InfoRow label="Distance" value={`${eta.distanceKm ?? "—"} km`} />
                                    <InfoRow label="Speed" value={`${eta.speedKmh ?? "—"} km/h`} />
                                    <InfoRow label="Remaining" value={`${eta.remainingMinutes ?? "—"} min`} />
                                    <InfoRow
                                        label="Arrival"
                                        value={
                                            eta.estimatedArrivalUtc
                                                ? new Date(eta.estimatedArrivalUtc).toLocaleString()
                                                : "—"
                                        }
                                    />
                                </div>
                            ) : (
                                <EmptyText text="ETA is not available yet." />
                            )}
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow border border-slate-200 overflow-hidden">
                        <div className="p-6 border-b">
                            <h2 className="text-2xl font-bold">Route History</h2>
                            <p className="text-slate-500 mt-1">
                                Recorded GPS movement points for this shipment.
                            </p>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-100">
                                    <tr>
                                        <th className="text-left px-4 py-3">#</th>
                                        <th className="text-left px-4 py-3">Latitude</th>
                                        <th className="text-left px-4 py-3">Longitude</th>
                                        <th className="text-left px-4 py-3">Recorded At</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {route.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="text-center py-8 text-slate-500">
                                                No route data available.
                                            </td>
                                        </tr>
                                    ) : (
                                        route.map((r, i) => (
                                            <tr key={i} className="border-t hover:bg-slate-50">
                                                <td className="px-4 py-3 font-medium">{i + 1}</td>
                                                <td className="px-4 py-3">{r.latitude ?? "—"}</td>
                                                <td className="px-4 py-3">{r.longitude ?? "—"}</td>
                                                <td className="px-4 py-3">
                                                    {r.recordedAt || r.createdAt
                                                        ? new Date(r.recordedAt || r.createdAt).toLocaleString()
                                                        : "—"}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

const StatCard = ({ title, value, subtitle, color = "blue" }) => {
    const colors = {
        blue: "bg-blue-50 text-blue-700",
        green: "bg-green-50 text-green-700",
        yellow: "bg-yellow-50 text-yellow-700",
        red: "bg-red-50 text-red-700",
        slate: "bg-slate-50 text-slate-700",
    };

    return (
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 hover:shadow-xl transition">
            <p className="text-slate-500">{title}</p>
            <div className={`inline-flex mt-3 px-3 py-1 rounded-xl font-black text-xl ${colors[color]}`}>
                {value ?? "—"}
            </div>
            <p className="text-slate-500 text-sm mt-3">{subtitle}</p>
        </div>
    );
};

const InfoRow = ({ label, value }) => (
    <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
            {label}
        </p>
        <p className="font-bold text-slate-900 mt-1 break-words">
            {value ?? "—"}
        </p>
    </div>
);

const EmptyText = ({ text }) => (
    <p className="text-slate-500">{text}</p>
);